import { useMemo } from 'react'
import { useMultiPmoEnabled } from '~/api/programTeam'
import { useVendorVisible } from '~/api/work_order'
import { useMsg } from '~/common/localizationUtils'
import { Filter, typedFilters } from '~/components/v2/FiltersSidebar/types'
import { NamedQueryFilter } from '~/custom_reports/types'
import { ChartCard } from '~/dashboards/ChartCard'
import { DashboardPageContainer, DashboardPageHeader } from '~/dashboards/common'
import { updateDashboardQueryParams, useChoroplethDataV2, useDashboardReport } from '~/dashboards/hooks'
import {
  BarChart,
  CHOROPLETH_REGION,
  CountryDataChoropleth,
  PieChart,
  RegionDataChoropleth,
  SimpleGrid
} from '~/design_system'
import { useDeepEqualEffect2 } from '~/hooks/useDeepEqualEffect'
import { featureFlags, useFeatureFlag } from '~/hooks/useFeatureFlag'
import { usePermittedFilters } from '~/hooks/usePermittedFilters'
import {
  getVendorFilter,
  getWoStatusFilter,
  locationFilter,
  resourceManagerFilter
} from '~/work_order_list/filtersConfig'
import filterAdapters from './filters/adapters/work_orders'
import { getBusinessUnitFilter, programTeamEntityFilter } from './filters/config'
import { useDashboardFilters } from './filters/hooks'
import { getFilterParams } from './filters/utils'

const useFiltersConfig = () => {
  const { data: multiPmoEnabled } = useMultiPmoEnabled()

  return useMemo(() => {
    const filters = [
      { ...getWoStatusFilter() },
      locationFilter,
      resourceManagerFilter,
      { ...getVendorFilter() },
      { ...getBusinessUnitFilter() }
    ]

    if (multiPmoEnabled?.enabled) {
      const programTeamEntityFilterConfig = typedFilters(programTeamEntityFilter as Filter)[0]
      filters.splice(-1, 0, programTeamEntityFilterConfig as any)
    }

    return filters
  }, [multiPmoEnabled?.enabled])
}

const useUpdateFiltersEffect = (filters: NamedQueryFilter[]) => {
  const queryFilters = getFilterParams(filters, filterAdapters)

  const bulkDashboardQueryParams = {
    work_orders_by_country: { filters: queryFilters },
    work_orders_by_state: { filters: queryFilters },
    work_orders_by_work_site: { filters: queryFilters },
    work_orders_by_vendors: { filters: queryFilters },
    work_orders_by_managers: { filters: queryFilters },
    work_orders_by_source: { filters: queryFilters }
  }

  useDeepEqualEffect2(() => {
    updateDashboardQueryParams(bulkDashboardQueryParams)
  }, [bulkDashboardQueryParams])
}

export const WorkOrders = () => {
  const msg = useMsg()
  const filtersConfig = useFiltersConfig()
  const permittedFilters = usePermittedFilters(filtersConfig)

  return (
    <>
      <DashboardPageHeader title={msg('term.workorder', { count: 999 })} />
      <DashboardPageContainer filtersConfig={permittedFilters}>
        <Charts />
      </DashboardPageContainer>
    </>
  )
}

const Charts = () => {
  const { isVendorVisible } = useVendorVisible()
  const businessUnitFilter = getBusinessUnitFilter()

  const filters = useDashboardFilters({
    [businessUnitFilter.id]: {
      operator: 'within_hierarchy',
      type: businessUnitFilter.type
    }
  })
  useUpdateFiltersEffect(filters)
  const { data: showUSMaps } = useFeatureFlag(featureFlags.SHOW_US_MAPS)
  const { data: showWorldMaps } = useFeatureFlag(featureFlags.SHOW_WORLD_MAPS)

  return (
    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
      {showWorldMaps && <WorkOrdersByCountry filters={filters} />}
      {showUSMaps && <WorkOrdersByState filters={filters} />}
      <WorkOrdersByWorkSite filters={filters} />
      {isVendorVisible && <WorkOrdersByVendors filters={filters} />}
      <WorkOrdersByManagers filters={filters} />
      <WorkOrdersBySource filters={filters} />
    </SimpleGrid>
  )
}

const WorkOrdersByWorkSite = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)
  return (
    <ChartCard
      queryId="WorkOrdersByWorkSite"
      reportKey="work_orders_by_work_site"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_worksite.title'),
        subTitle: msg('dashboards.chart.workorders_by_worksite.sub_title'),
        format: 'number'
      }}
    />
  )
}

const WorkOrdersByVendors = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)
  return (
    <ChartCard
      queryId="WorkOrdersByVendor"
      reportKey="work_orders_by_vendors"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_vendor.title'),
        subTitle: msg('dashboards.chart.workorders_by_vendor.sub_title'),
        format: 'number'
      }}
    />
  )
}

const WorkOrdersByManagers = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const queryFilters = getFilterParams(filters, filterAdapters)
  return (
    <ChartCard
      queryId="WorkOrdersByManagers"
      reportKey="work_orders_by_managers"
      filters={queryFilters}
      chart={BarChart}
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_resource_manager.title'),
        subTitle: msg('dashboards.chart.workorders_by_resource_manager.sub_title'),
        format: 'number'
      }}
    />
  )
}

export const WorkOrdersByState = ({
  filters,
  projectionScale
}: {
  filters: NamedQueryFilter[]
  projectionScale?: number
}) => {
  const msg = useMsg()
  const reportKey = 'work_orders_by_state'
  const queryFilters = getFilterParams(filters, filterAdapters)
  const queryData = useChoroplethDataV2({
    id: 'WorkOrdersByState',
    queryName: 'work_orders_by_state',
    dashboardQueryParams: { filters: queryFilters }
  })

  return (
    <CountryDataChoropleth
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_state.title'),
        subTitle: msg('dashboards.chart.workorders_by_state.sub_title'),
        format: 'number'
      }}
      reportKey={reportKey}
      projectionScale={projectionScale}
      {...queryData}
    />
  )
}

export const WorkOrdersByCountry = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'work_orders_by_country'
  const queryFilters = getFilterParams(filters, filterAdapters)
  const queryData = useDashboardReport({
    id: 'WorkOrdersByCountry',
    queryName: reportKey,
    dashboardQueryParams: { filters: queryFilters },
    useTransform: false
  })

  return (
    <RegionDataChoropleth
      region={CHOROPLETH_REGION.world}
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_country.title'),
        subTitle: msg('dashboards.chart.workorders_by_country.sub_title'),
        format: 'number'
      }}
      reportKey={reportKey}
      {...queryData}
    />
  )
}

export const WorkOrdersBySource = ({ filters }: { filters: NamedQueryFilter[] }) => {
  const msg = useMsg()
  const reportKey = 'work_orders_by_source'
  const queryFilters = getFilterParams(filters, filterAdapters)
  return (
    <ChartCard
      queryId="WorkOrdersBySource"
      filters={queryFilters}
      reportKey={reportKey}
      chart={PieChart}
      chartConfig={{
        title: msg('dashboards.chart.workorders_by_source_type.title'),
        subTitle: msg('dashboards.chart.workorders_by_source_type.sub_title'),
        format: 'number'
      }}
    />
  )
}
